import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  completeRevisions,
  fetchChapters,
  markLecture,
  removeChapter,
  updateChapterLectures,
} from "@/lib/chapters.functions";
import { friendlyError, notifySaved, trackSave } from "@/lib/save-feedback";
import { localDateString } from "@/lib/app-types";
import { usePremium } from "@/components/billing/premium-lock";
import { useQuotaUpgrade } from "@/components/billing/use-quota-upgrade";
import {
  LECTURE_REVISION_INTERVALS,
  addDays,
  type LectureRevision,
  type StudyChapter,
} from "@/lib/lecture-plan";

const KEY = ["chapters"] as const;

export function useChapters() {
  const queryClient = useQueryClient();
  const today = localDateString();
  const [detailId, setDetailId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(new Set<string>());
  const showQuotaUpgrade = useQuotaUpgrade();

  const { data, isPending } = useQuery({
    queryKey: KEY,
    queryFn: () => fetchChapters(),
    staleTime: 5 * 60_000,
  });

  const chapters = useMemo<StudyChapter[]>(() => data ?? [], [data]);
  const detail = chapters.find((c) => c.id === detailId) ?? null;

  /** Applies an instant local edit and returns a rollback snapshot. */
  const patchCache = useCallback(
    (updater: (chapters: StudyChapter[]) => StudyChapter[]) => {
      const previous = queryClient.getQueryData<StudyChapter[]>(KEY);
      queryClient.setQueryData<StudyChapter[]>(KEY, (old) => updater(old ?? []));
      return previous;
    },
    [queryClient],
  );

  const settle = useCallback(
    (touchesQuestions = false) => {
      queryClient.invalidateQueries({ queryKey: KEY });
      if (touchesQuestions) {
        queryClient.invalidateQueries({ queryKey: ["question-chapters"] });
      }
    },
    [queryClient],
  );

  const rollback = useCallback(
    (previous: StudyChapter[] | undefined, err: Error) => {
      if (previous) queryClient.setQueryData(KEY, previous);
      showQuotaUpgrade(err);
      setError(friendlyError(err));
    },
    [queryClient, showQuotaUpgrade],
  );

  const lecture = useMutation({
    mutationFn: (vars: { lectureId: string; completedOn: string | null }) =>
      markLecture({ data: vars }),
    retry: 2,
    onMutate: (vars) => {
      setError(null);
      const stop = trackSave("mark-lecture");
      const previous = patchCache((list) =>
        list.map((chapter) => {
          if (!chapter.lectures.some((l) => l.id === vars.lectureId)) return chapter;
          const number = chapter.lectures.find((l) => l.id === vars.lectureId)?.number ?? 0;
          const kept = chapter.revisions.filter(
            (r) => r.lectureId !== vars.lectureId || r.status === "completed",
          );
          const fresh: LectureRevision[] = vars.completedOn
            ? LECTURE_REVISION_INTERVALS.map((days, index) => ({
                id: `temp-${vars.lectureId}-${index}`,
                lectureId: vars.lectureId,
                lectureNumber: number,
                revisionNumber: index + 1,
                intervalDays: days,
                dueDate: addDays(vars.completedOn as string, days),
                status: "pending",
                completedAt: null,
              }))
            : [];
          return {
            ...chapter,
            lectures: chapter.lectures.map((l) =>
              l.id === vars.lectureId ? { ...l, completedOn: vars.completedOn } : l,
            ),
            revisions: [...kept, ...fresh],
          };
        }),
      );
      notifySaved(vars.completedOn ? "Lecture marked complete" : "Lecture reopened");
      return { previous, stop };
    },
    onError: (err: Error, _vars, ctx) => {
      ctx?.stop("error");
      rollback(ctx?.previous, err);
    },
    onSuccess: (_res, _vars, ctx) => ctx?.stop("ok"),
    onSettled: () => settle(true),
  });

  const revise = useMutation({
    mutationFn: (revisionIds: string[]) => completeRevisions({ data: { revisionIds, today } }),
    retry: 2,
    onMutate: (revisionIds) => {
      setError(null);
      const stop = trackSave("complete-revisions");
      const ids = new Set(revisionIds);
      const now = new Date().toISOString();
      const previous = patchCache((list) =>
        list.map((chapter) =>
          chapter.revisions.some((r) => ids.has(r.id))
            ? {
                ...chapter,
                revisions: chapter.revisions.map((r) =>
                  ids.has(r.id)
                    ? { ...r, status: "completed" as const, completedAt: now }
                    : r,
                ),
              }
            : chapter,
        ),
      );
      notifySaved(revisionIds.length > 1 ? "Revisions marked done" : "Revision marked done");
      return { previous, stop };
    },
    onError: (err: Error, _vars, ctx) => {
      ctx?.stop("error");
      rollback(ctx?.previous, err);
    },
    onSuccess: (_res, _vars, ctx) => ctx?.stop("ok"),
    onSettled: () => settle(),
  });

  const lectures = useMutation({
    mutationFn: (vars: { chapterId: string; totalLectures: number }) =>
      updateChapterLectures({ data: vars }),
    retry: 2,
    onMutate: (vars) => {
      setError(null);
      const stop = trackSave("chapter-lectures");
      const previous = patchCache((list) =>
        list.map((chapter) => {
          if (chapter.id !== vars.chapterId) return chapter;
          const kept = chapter.lectures.filter((l) => l.number <= vars.totalLectures);
          const extra = Array.from(
            { length: Math.max(0, vars.totalLectures - kept.length) },
            (_, i) => ({
              id: `temp-lecture-${chapter.id}-${kept.length + i + 1}`,
              number: kept.length + i + 1,
              completedOn: null,
            }),
          );
          return { ...chapter, totalLectures: vars.totalLectures, lectures: [...kept, ...extra] };
        }),
      );
      notifySaved("Lecture count updated");
      return { previous, stop };
    },
    onError: (err: Error, _vars, ctx) => {
      ctx?.stop("error");
      rollback(ctx?.previous, err);
    },
    onSuccess: (_res, _vars, ctx) => ctx?.stop("ok"),
    onSettled: () => settle(),
  });

  const destroy = useMutation({
    mutationFn: (chapterId: string) => removeChapter({ data: { chapterId } }),
    retry: 2,
    onMutate: (chapterId) => {
      setError(null);
      const stop = trackSave("delete-chapter");
      setDetailId(null);
      const previous = patchCache((list) => list.filter((c) => c.id !== chapterId));
      notifySaved("Chapter deleted");
      return { previous, stop };
    },
    onError: (err: Error, _vars, ctx) => {
      ctx?.stop("error");
      rollback(ctx?.previous, err);
    },
    onSuccess: (_res, _vars, ctx) => ctx?.stop("ok"),
    onSettled: () => settle(true),
  });

  const { requirePremium } = usePremium();

  /** Drops repeat taps on the same target while its write is still in flight. */
  const once = useCallback((token: string, run: () => void) => {
    if (!requirePremium()) return;
    if (inFlight.current.has(token)) return;
    inFlight.current.add(token);
    run();
    window.setTimeout(() => inFlight.current.delete(token), 600);
  }, [requirePremium]);

  return {
    today,
    chapters,
    isPending,
    error,
    detail,
    openDetail: setDetailId,
    closeDetail: () => setDetailId(null),
    /** Kept for API compatibility — the UI no longer blocks on saves. */
    busy: false,
    syncing: lecture.isPending || revise.isPending || lectures.isPending || destroy.isPending,
    markLecture: (lectureId: string, completedOn: string | null) =>
      once(`lecture:${lectureId}`, () => lecture.mutate({ lectureId, completedOn })),
    completeRevisions: (ids: string[]) =>
      once(`revisions:${ids.join(",")}`, () => revise.mutate(ids)),
    setLectureCount: (chapterId: string, totalLectures: number) =>
      once(`lectures:${chapterId}:${totalLectures}`, () =>
        lectures.mutate({ chapterId, totalLectures }),
      ),
    deleteChapter: (chapterId: string) => once(`delete:${chapterId}`, () => destroy.mutate(chapterId)),
  };
}